// First Created - 22 Oct 2026
// Author - Atul Pandey

import {
    createSelector,
    createEntityAdapter,
} from '@reduxjs/toolkit'

import { apiSlice } from '@/app/api/apiSlice'


/*
|--------------------------------------------------------------------------
| 01 - Evaluation Categories Adapter
|--------------------------------------------------------------------------
*/

const evalCategorysAdapter = createEntityAdapter({})

const initialState =
    evalCategorysAdapter.getInitialState()


/*
|--------------------------------------------------------------------------
| 02 - Evaluation Categories API Slice
|--------------------------------------------------------------------------
*/

export const evalCategorysApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({

        /*
        |--------------------------------------------------------------------------
        | Get Evaluation Categories
        |--------------------------------------------------------------------------
        |
        | active:
        |   true  - Active categories
        |   false - Inactive categories
        |   all   - All categories
        |
        */

        getEvalCategories: builder.query({

            query: ({
                active = true,
            } = {}) => ({

                url: '/evalCategories/getEvalCategories',

                params: {
                    active: String(active),
                },

                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                },

            }),

            transformResponse: responseData => {

                const loadedEvalCategories = responseData.map(
                    evalCategory => ({

                        ...evalCategory,

                        id: evalCategory._id,

                    })
                )

                return evalCategorysAdapter.setAll(
                    initialState,
                    loadedEvalCategories
                )

            },

            providesTags: (result) => {

                if (result?.ids) {

                    return [
                        {
                            type: 'EvalCategory',
                            id: 'LIST'
                        },

                        ...result.ids.map(
                            id => ({
                                type: 'EvalCategory',
                                id
                            })
                        )
                    ]

                }

                return [
                    {
                        type: 'EvalCategory',
                        id: 'LIST'
                    }
                ]

            },

        }),


        /*
        |--------------------------------------------------------------------------
        | Get Evaluation Category By ID
        |--------------------------------------------------------------------------
        */

        getEvalCategoryById: builder.query({

            query: ({ id }) => ({

                url: '/evalCategories/getEvalCategoryById',

                params: {
                    id
                },

                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                },

            }),

            providesTags: (result, error, arg) => [
                {
                    type: 'EvalCategory',
                    id: arg?.id
                }
            ],

        }),


        /*
        |--------------------------------------------------------------------------
        | Create New Evaluation Category
        |--------------------------------------------------------------------------
        */

        createNewEvalCategory: builder.mutation({

            query: initialEvalCategory => ({

                url: '/evalCategories/createNewEvalCategory',

                method: 'POST',

                body: {
                    ...initialEvalCategory
                },

            }),

            invalidatesTags: [
                {
                    type: 'EvalCategory',
                    id: 'LIST'
                }
            ],

        }),


        /*
        |--------------------------------------------------------------------------
        | Update Evaluation Category
        |--------------------------------------------------------------------------
        */

        updateEvalCategory: builder.mutation({

            query: initialEvalCategory => ({

                url: '/evalCategories/updateEvalCategory',

                method: 'PUT',

                body: {
                    ...initialEvalCategory
                },

            }),

            invalidatesTags: (result, error, arg) => [
                {
                    type: 'EvalCategory',
                    id: 'LIST'
                },
                {
                    type: 'EvalCategory',
                    id: arg.id
                }
            ],

        }),


        /*
        |--------------------------------------------------------------------------
        | Update Evaluation Category Status
        |--------------------------------------------------------------------------
        */

        updateEvalCategoryStatus: builder.mutation({

            query: ({ id, active }) => ({

                url: '/evalCategories/updateEvalCategoryStatus',

                method: 'PATCH',

                body: {
                    id,
                    active
                },

            }),

            invalidatesTags: (result, error, arg) => [
                {
                    type: 'EvalCategory',
                    id: 'LIST'
                },
                {
                    type: 'EvalCategory',
                    id: arg.id
                },
                {
                    type: 'EvalDimension',
                    id: 'LIST'
                }
            ],

        }),

        /*
        |--------------------------------------------------------------------------
        | 29 Oct 2026 - Hard Delete Evaluation Category
        |--------------------------------------------------------------------------
        |
        | Backend rejects deletion with 409 if referenced.
        |
        |--------------------------------------------------------------------------
        */

        deleteEvalCategory: builder.mutation({

            query: ({ id }) => ({

                url: '/evalCategories/deleteEvalCategory',

                method: 'DELETE',

                body: {
                    id,
                },

            }),

            invalidatesTags: (result, error, arg) =>
                error
                    ? []
                    : [
                        {
                            type: 'EvalCategory',
                            id: 'LIST',
                        },
                        {
                            type: 'EvalCategory',
                            id: arg.id,
                        },
                        {
                            type: 'EvalDimension',
                            id: 'LIST',
                        },
                    ],

        }),

    }),
})


/*
|--------------------------------------------------------------------------
| 03 - Export Hooks
|--------------------------------------------------------------------------
*/

export const {

    useGetEvalCategoriesQuery,
    useLazyGetEvalCategoriesQuery,

    useGetEvalCategoryByIdQuery,

    useCreateNewEvalCategoryMutation,
    useUpdateEvalCategoryMutation,
    useUpdateEvalCategoryStatusMutation,

    useDeleteEvalCategoryMutation, // 29 Oct 2026

} = evalCategorysApiSlice


/*
|--------------------------------------------------------------------------
| 04 - Category Selectors
|--------------------------------------------------------------------------
|
| RTK Query maintains separate caches for each query argument.
|
| The selector factory accepts the same argument used by
| useGetEvalCategoriesQuery().
|
|--------------------------------------------------------------------------
*/

export const selectEvalCategoriesResult = (arg = {}) =>
    evalCategorysApiSlice.endpoints
        .getEvalCategories
        .select(arg)


export const makeSelectEvalCategoriesData = (arg = {}) =>
    createSelector(
        selectEvalCategoriesResult(arg),
        result => result.data
    )


export const makeEvalCategorySelectors = (arg = {}) => {

    const selectData = makeSelectEvalCategoriesData(arg)

    return evalCategorysAdapter.getSelectors(
        state => selectData(state) ?? initialState
    )

}


/*
|--------------------------------------------------------------------------
| 05 - Default Active Category Selectors
|--------------------------------------------------------------------------
|
| These correspond to the default query:
|
| useGetEvalCategoriesQuery()
|
|--------------------------------------------------------------------------
*/

export const {

    selectAll: selectAllEvalCategories,
    selectById: selectEvalCategoryById,
    selectIds: selectEvalCategoryIds,

} = makeEvalCategorySelectors()